import type { SceneHost, SceneObject } from "../SceneHost";
import { getPenSettings } from "../penSession";
import {
  clonePoints,
  computePathBBox,
  createPathPreviewSvg,
  pathLength,
  softenStrokeCorners,
  chaikinCornerCut,
  windowSmoothInterior,
  thinPathPoints,
  simplifyPolyline,
  compactStrokePointsForStorage,
  trimStrokeTipHook,
  type PathPoint,
} from "../pathGeometry";
import {
  applyDocumentContext,
  documentContextForStroke,
  ensureAnnotationAboveDocument,
} from "../document/documentContext";
import { applyFrameContext, frameAtPoint } from "../frame/frameContext";
import { snapSegmentEnd } from "../segmentGeometry";
import { isBoardEditorChrome, isBoardWidgetChromeExceptCalcPlot, isCalculatorKeypadAt } from "./boardWidgetChrome";
import { noteDiagAction } from "../../lib/board/boardDiagTrail";

/**
 * Короче этого (в пикселях ЭКРАНА) — это тап, а не штрих: ставим точку.
 * Порог маленький и экранный: раньше он был 10 экранных px и ещё 6 единиц
 * доски, поэтому маленькая чёрточка (минус, запятая, чёрточка дроби)
 * превращалась в точку — особенно на увеличенном масштабе, где 6 единиц
 * доски занимают на экране много пикселей.
 * Движением считается смещение примерно от 1 px, так что дрожание руки
 * при постановке точки чёрточкой не станет.
 */
export const DOT_SLOP_SCREEN_PX = 4;

/**
 * Ставить точку вместо штриха?
 * Длина считается в единицах доски, а порог задан в экранных пикселях,
 * поэтому на любом масштабе «точкой» становится одно и то же на вид
 * движение, а маленькая чёрточка остаётся чёрточкой.
 */
export function strokeIsDot(opts: {
  wasClick: boolean;
  strokeLen: number;
  worldPerScreenPx: number;
}): boolean {
  if (opts.wasClick) return true;
  return opts.strokeLen < DOT_SLOP_SCREEN_PX * opts.worldPerScreenPx;
}
/** Лимит схемы API — иначе ops → invalid_input и штрих откатывается. */
const MAX_SCHEMA_PATH_POINTS = 8192;
/** Мягкий потолок во время рисования — держим UI отзывчивым. */
const MAX_LIVE_INK_POINTS = 3500;
const TARGET_COMMIT_POINTS = 1800;

/** Две точки рядом + round cap → визуальная точка (схема требует ≥2 точек). */
function makeDotPoints(center: PathPoint, strokeWidth: number): PathPoint[] {
  const e = Math.max(0.25, strokeWidth * 0.04);
  return [
    [center[0] - e, center[1]],
    [center[0] + e, center[1]],
  ];
}

type StrokeSmoothing = {
  minPointDistScreen: number;
  streamline: number;
  softenAmount: number;
  softenPasses: number;
  tipKeep: number;
  windowHalf: number;
  chaikinIters: number;
};

/**
 * Уже положенные точки НЕ двигаем (иначе верёвочка).
 * Одна и та же полировка для live и commit — без «прыжка» при отпускании.
 */
function smoothingForPointer(pointerType: string): StrokeSmoothing {
  if (pointerType === "mouse") {
    return {
      minPointDistScreen: 0.55,
      streamline: 0.18,
      softenAmount: 0.52,
      softenPasses: 2,
      tipKeep: 6,
      windowHalf: 2,
      chaikinIters: 1,
    };
  }
  if (pointerType === "pen") {
    return {
      minPointDistScreen: 1.05,
      streamline: 0.22,
      softenAmount: 0.36,
      softenPasses: 2,
      tipKeep: 5,
      windowHalf: 2,
      chaikinIters: 1,
    };
  }
  return {
    minPointDistScreen: 0.75,
    streamline: 0.2,
    softenAmount: 0.42,
    softenPasses: 2,
    tipKeep: 5,
    windowHalf: 2,
    chaikinIters: 1,
  };
}

function streamlineT(streamline: number): number {
  return 0.15 + (1 - streamline) * 0.85;
}

function streamlinePoints(rawPoints: PathPoint[], t: number): PathPoint[] {
  if (rawPoints.length === 0) return [];
  if (rawPoints.length === 1 || t >= 0.999) return clonePoints(rawPoints);

  const out: PathPoint[] = [[rawPoints[0]![0], rawPoints[0]![1]]];
  let prev = out[0]!;
  for (let i = 1; i < rawPoints.length; i++) {
    const raw = rawPoints[i]!;
    const point: PathPoint = [
      prev[0] + (raw[0] - prev[0]) * t,
      prev[1] + (raw[1] - prev[1]) * t,
    ];
    if (Math.hypot(point[0] - prev[0], point[1] - prev[1]) < 1e-4) continue;
    out.push(point);
    prev = point;
  }
  const lastRaw = rawPoints[rawPoints.length - 1]!;
  if (out.length === 0) return [[lastRaw[0], lastRaw[1]]];
  out[out.length - 1] = [lastRaw[0], lastRaw[1]];
  return out;
}

function polishStroke(raw: PathPoint[], cfg: StrokeSmoothing): PathPoint[] {
  if (raw.length < 3) return clonePoints(raw);
  let pts = streamlinePoints(raw, streamlineT(cfg.streamline));
  pts = softenStrokeCorners(pts, cfg.softenAmount, cfg.softenPasses, cfg.tipKeep);
  if (cfg.windowHalf > 0) {
    pts = windowSmoothInterior(pts, cfg.windowHalf);
  }
  if (cfg.chaikinIters > 0 && pts.length >= 4) {
    pts = chaikinCornerCut(pts, cfg.chaikinIters);
    pts = windowSmoothInterior(pts, Math.max(1, cfg.windowHalf - 1));
  }
  return pts;
}

/** Тот же пайплайн полировки, что и при commit — без «прыжка» и угловатого polyline. */
function buildLivePreviewPoints(
  raw: PathPoint[],
  cfg: StrokeSmoothing,
  shiftHeld: boolean,
  strokeWidth: number
): PathPoint[] {
  if (raw.length < 1) return [];
  if (raw.length < 2) {
    return makeDotPoints(raw[0]!, strokeWidth);
  }
  if (shiftHeld) {
    const a = raw[0]!;
    const b = snapSegmentEnd(a, raw[raw.length - 1]!, true);
    return [
      [Math.round(a[0] * 10) / 10, Math.round(a[1] * 10) / 10],
      [Math.round(b[0] * 10) / 10, Math.round(b[1] * 10) / 10],
    ];
  }
  let src = raw;
  if (src.length > 1600) {
    src = thinPathPoints(src, 0.38);
  }
  return polishStroke(src, cfg);
}

/** Тот же пайплайн для commit (полная полировка). */
function buildFinalStrokePoints(
  raw: PathPoint[],
  cfg: StrokeSmoothing,
  shiftHeld: boolean,
  strokeWidth: number
): PathPoint[] {
  if (raw.length < 1) return [];
  if (raw.length < 2) {
    return makeDotPoints(raw[0]!, strokeWidth);
  }
  if (shiftHeld) {
    const a = raw[0]!;
    const b = snapSegmentEnd(a, raw[raw.length - 1]!, true);
    return [
      [Math.round(a[0] * 10) / 10, Math.round(a[1] * 10) / 10],
      [Math.round(b[0] * 10) / 10, Math.round(b[1] * 10) / 10],
    ];
  }
  // Для очень длинных штрихов сначала чуть проредим вход — иначе кадр подвисает.
  let src = raw;
  if (src.length > 1600) {
    src = thinPathPoints(src, 0.38);
  }
  return clampPointsForSchema(polishStroke(src, cfg), TARGET_COMMIT_POINTS);
}

function finalizeCommitPoints(points: PathPoint[], wPerPx: number): PathPoint[] {
  // Только кончик: срезать отскок при pointerup (~6 px экрана).
  return trimStrokeTipHook(points, Math.max(0.5, 6 * wPerPx));
}

/** Гарантированно ≤ maxPoints и без NaN — иначе сервер откатывает add_object. */
function clampPointsForSchema(points: PathPoint[], maxPoints: number): PathPoint[] {
  let pts = points.filter(
    ([x, y]) => Number.isFinite(x) && Number.isFinite(y)
  ) as PathPoint[];
  if (pts.length < 2) return pts;

  pts = compactStrokePointsForStorage(pts);
  if (pts.length > maxPoints) {
    pts = thinPathPoints(pts, 0.35);
  }
  if (pts.length > maxPoints) {
    pts = simplifyPolyline(pts, 0.45);
  }
  if (pts.length > maxPoints) {
    const step = Math.ceil(pts.length / maxPoints);
    const out: PathPoint[] = [pts[0]!];
    for (let i = step; i < pts.length - 1; i += step) {
      out.push(pts[i]!);
    }
    out.push(pts[pts.length - 1]!);
    pts = out;
  }
  if (pts.length > maxPoints) {
    pts = [pts[0]!, ...pts.slice(-(maxPoints - 1))];
  }
  return pts.map(
    ([x, y]) => [Math.round(x * 100) / 100, Math.round(y * 100) / 100] as PathPoint
  );
}

export type PenToolBinding = {
  destroy: () => void;
};

function worldUnitsPerScreenPx(host: SceneHost): number {
  const a = host.stagePoint(0, 0);
  const b = host.stagePoint(100, 0);
  const w = Math.abs(b.x - a.x);
  return w > 1e-6 ? w / 100 : 1;
}

export function bindPenTool(host: SceneHost): PenToolBinding {
  let drawing = false;
  /** Только append — никогда не двигаем прошлые точки. */
  let inkPoints: PathPoint[] = [];
  /** Курсор чуть впереди последней ink-точки (< minDist). */
  let tipPoint: PathPoint | null = null;
  /** Было ли реальное движение пера (иначе — клик → точка). */
  let strokeMoved = false;
  let shiftHeld = false;
  let activePointerId: number | null = null;
  let activeSmoothing = smoothingForPointer("pen");
  let previewRaf = 0;
  let previewDirty = false;
  const preview = createPathPreviewSvg();

  function clearPreview() {
    if (previewRaf) {
      cancelAnimationFrame(previewRaf);
      previewRaf = 0;
    }
    previewDirty = false;
    preview.root.remove();
  }

  function samplePoints(): PathPoint[] {
    if (!tipPoint) return inkPoints;
    if (inkPoints.length === 0) return [tipPoint];
    const last = inkPoints[inkPoints.length - 1]!;
    if (Math.hypot(tipPoint[0] - last[0], tipPoint[1] - last[1]) < 1e-4) {
      return inkPoints;
    }
    // Короткий кончик к курсору — без перетаскивания всей линии.
    return inkPoints.concat([tipPoint]);
  }

  function computeLivePreviewPoints(): PathPoint[] {
    const pen = getPenSettings();
    return buildLivePreviewPoints(
      samplePoints(),
      activeSmoothing,
      shiftHeld,
      pen.strokeWidth
    );
  }

  function computeCommitPoints(): PathPoint[] {
    const pen = getPenSettings();
    return buildFinalStrokePoints(
      samplePoints(),
      activeSmoothing,
      shiftHeld,
      pen.strokeWidth
    );
  }

  function flushPreview() {
    previewRaf = 0;
    if (!drawing || !previewDirty) return;
    previewDirty = false;
    const pen = getPenSettings();
    const drawPts = computeLivePreviewPoints();
    preview.setPoints(
      drawPts,
      pen.stroke,
      pen.strokeWidth,
      pen.strokeLinecap,
      pen.strokeDash ?? "solid",
      "curve"
    );
  }

  function schedulePreview() {
    previewDirty = true;
    if (previewRaf) return;
    previewRaf = requestAnimationFrame(flushPreview);
  }

  function minDistWorld(): number {
    return Math.max(0.1, activeSmoothing.minPointDistScreen * worldUnitsPerScreenPx(host));
  }

  function thinInkInPlace() {
    if (inkPoints.length <= MAX_LIVE_INK_POINTS) return;
    const tip = tipPoint ?? inkPoints[inkPoints.length - 1]!;
    let thinned = thinPathPoints(inkPoints, 0.45);
    if (thinned.length > MAX_LIVE_INK_POINTS) {
      thinned = simplifyPolyline(thinned, 0.6);
    }
    if (thinned.length > MAX_LIVE_INK_POINTS) {
      const step = Math.ceil(thinned.length / MAX_LIVE_INK_POINTS);
      const out: PathPoint[] = [thinned[0]!];
      for (let i = step; i < thinned.length - 1; i += step) out.push(thinned[i]!);
      out.push(thinned[thinned.length - 1]!);
      thinned = out;
    }
    const last = thinned[thinned.length - 1]!;
    if (Math.hypot(tip[0] - last[0], tip[1] - last[1]) > 1e-4) {
      thinned.push([tip[0], tip[1]]);
    }
    inkPoints = thinned;
  }

  /**
   * Только добавляем точку курсора — без while-заполнения хорд
   * (хорды при быстрой кривой выглядят как «прямая за курсором»).
   */
  function appendSample(raw: PathPoint) {
    tipPoint = [raw[0], raw[1]];
    if (inkPoints.length === 0) {
      inkPoints = [[raw[0], raw[1]]];
      return;
    }
    const last = inkPoints[inkPoints.length - 1]!;
    const dist = Math.hypot(raw[0] - last[0], raw[1] - last[1]);
    if (dist < minDistWorld()) return;
    strokeMoved = true;
    inkPoints.push([raw[0], raw[1]]);
    if (inkPoints.length > MAX_LIVE_INK_POINTS) {
      thinInkInPlace();
    }
  }

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "pen") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChromeExceptCalcPlot(ev.target)) return;
    if (ev.button !== 0) return;
    ev.preventDefault();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    drawing = true;
    activePointerId = ev.pointerId;
    activeSmoothing = smoothingForPointer(ev.pointerType || "mouse");
    shiftHeld = ev.shiftKey;
    strokeMoved = false;
    inkPoints = [[pt.x, pt.y]];
    tipPoint = [pt.x, pt.y];
    noteDiagAction("pen_start", ev.pointerType || "mouse");
    host.selectionOverlay.appendChild(preview.root);
    previewDirty = true;
    flushPreview();

    try {
      host.hitSurface.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  }

  function onPointerMove(ev: PointerEvent) {
    if (!drawing || ev.pointerId !== activePointerId) return;
    shiftHeld = ev.shiftKey;
    if (isCalculatorKeypadAt(ev.clientX, ev.clientY)) {
      finishStroke(true);
      return;
    }

    const coalesced =
      typeof ev.getCoalescedEvents === "function" ? ev.getCoalescedEvents() : null;
    if (coalesced && coalesced.length > 0) {
      for (const ce of coalesced) {
        const pt = host.stagePoint(ce.clientX, ce.clientY);
        appendSample([pt.x, pt.y]);
      }
    } else {
      const pt = host.stagePoint(ev.clientX, ev.clientY);
      appendSample([pt.x, pt.y]);
    }
    schedulePreview();
  }

  function finishStroke(commit: boolean) {
    if (!drawing) return;
    drawing = false;
    activePointerId = null;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerUp);

    // Полная полировка только при commit — live-превью лёгкое, без «хвостика» на отпускании.
    const wPerPxEarly = worldUnitsPerScreenPx(host);
    const displayPts = finalizeCommitPoints(
      clonePoints(computeCommitPoints()),
      wPerPxEarly
    );
    const startPt = inkPoints[0] ? ([inkPoints[0][0], inkPoints[0][1]] as PathPoint) : null;
    const wasClick = !strokeMoved;

    clearPreview();
    tipPoint = null;
    inkPoints = [];
    strokeMoved = false;

    if (!commit) {
      noteDiagAction("pen_cancel", "no_commit");
      return;
    }

    const pen = getPenSettings();
    const wPerPx = wPerPxEarly;
    // Порог в единицах доски, но считается из экранного: на любом масштабе
    // «точкой» становится одно и то же на вид движение.
    const dotSlopWorld = DOT_SLOP_SCREEN_PX * wPerPx;
    const strokeLen = displayPts.length >= 2 ? pathLength(displayPts) : 0;
    // Точка: чистый клик или реально короткий штрих.
    // Не смотрим start↔end — у замкнутой линии концы рядом, но путь длинный.
    const asDot =
      Boolean(startPt) && strokeIsDot({ wasClick, strokeLen, worldPerScreenPx: wPerPx });

    let finalPts: PathPoint[];
    if (asDot && startPt) {
      finalPts = makeDotPoints(startPt, pen.strokeWidth);
    } else {
      finalPts = displayPts;
      if (finalPts.length < 2) {
        // Микродраг: после сглаживания точек мало, но жест был — всё равно точка
        if (startPt) finalPts = makeDotPoints(startPt, pen.strokeWidth);
        else return;
      } else if (pathLength(finalPts) < dotSlopWorld) {
        if (startPt) finalPts = makeDotPoints(startPt, pen.strokeWidth);
        else return;
      }
    }

    if (finalPts.length > MAX_SCHEMA_PATH_POINTS) {
      finalPts = clampPointsForSchema(finalPts, MAX_SCHEMA_PATH_POINTS);
    }
    if (finalPts.length < 2) return;

    const bbox = computePathBBox(finalPts, pen.strokeWidth);
    const object: SceneObject = {
      id: crypto.randomUUID(),
      type: "path",
      x: bbox.x,
      y: bbox.y,
      w: Math.max(1, bbox.w),
      h: Math.max(1, bbox.h),
      points: clonePoints(finalPts),
      style: {
        stroke: pen.stroke,
        strokeWidth: pen.strokeWidth,
        strokeLinecap: pen.strokeLinecap,
        strokeLinejoin: "round",
        strokeDash: pen.strokeDash ?? "solid",
      },
    };

    applyDocumentContext(object, documentContextForStroke(host.getObjects(), finalPts));
    ensureAnnotationAboveDocument(object, host.getObjects());
    applyFrameContext(object, frameAtPoint(host.getObjects(), finalPts[0]![0], finalPts[0]![1]));

    noteDiagAction(
      commit ? "pen_commit" : "pen_cancel",
      `pts=${finalPts.length}${asDot ? ";dot" : ""}`
    );
    void host.addObject(object, `pen-${object.id}`);
  }

  function onPointerUp(ev: PointerEvent) {
    if (activePointerId !== null && ev.pointerId !== activePointerId) return;
    finishStroke(true);
  }

  function onViewportGestureStart() {
    finishStroke(false);
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("wb:viewport-gesture-start", onViewportGestureStart);

  return {
    destroy() {
      finishStroke(false);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("wb:viewport-gesture-start", onViewportGestureStart);
    },
  };
}
